import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import api from "../../api";
import { createAsyncThunkInstance } from "../createAsyncThunkInstance";
import { ICommonInitialState } from "../../shared/types/storeTypes";
import { AxiosError } from "axios";
import {
  BlacklistAction,
  ISingleCandidate,
  IUpdateCandidateBody,
} from "../../api/candidate/types";
import { SortingTypes } from "../../helpers/SortingTypes";
import { IVacancyApplication } from "../../api/vacancyApplications/types";

interface ICandidatesState extends ICommonInitialState {
  candidates: ISingleCandidate[];
  candidatesByVacancy: IVacancyApplication[];
  currentCandidate: ISingleCandidate | null;
}

const initialState: ICandidatesState = {
  candidates: [],
  candidatesByVacancy: [],
  currentCandidate: null,
  isLoading: false,
  error: null,
};

export const fetchAllCandidates = createAsyncThunkInstance<
  { count: number; items: ISingleCandidate[] },
  {
    limit: number;
    offset: number;
    search?: string;
    sort_column?: string;
    sort_direction?: SortingTypes;
    channel_id?: number;
    department_id?: number;
    skill?: string[];
    source_id?: number;
    status?: string;
    created_by?: number;
    created_at_from?: string;
    created_at_to?: string;
    responsible_recruiter_id?: number;
  }
>(
  "candidates/fetchAllCandidates",
  async ({
    limit,
    offset,
    search,
    sort_column,
    sort_direction,
    channel_id,
    department_id,
    skill,
    source_id,
    status,
    created_by,
    created_at_from,
    created_at_to,
    responsible_recruiter_id,
  }) => {
    const response = await api.candidates.getCandidates(search, {
      limit,
      offset,
      sort_column,
      sort_direction,
      channel_id,
      department_id,
      skill,
      source_id,
      status,
      created_by,
      created_at_from,
      created_at_to,
      responsible_recruiter_id,
    });
    return response;
  }
);

export const fetchAllCandidatesByVacancy = createAsyncThunkInstance<
  IVacancyApplication[],
  { id: string; sort_column?: string; sort_direction?: SortingTypes }
>(
  "candidates/fetchAllCandidatesByVacancy",
  async ({ id, sort_column, sort_direction }) => {
    return await api.candidates.getCandidatesByVacancy(id, {
      sort_direction,
      sort_column,
    });
  }
);

export const fetchSingleCandidate = createAsyncThunkInstance<
  ISingleCandidate,
  number
>("candidates/fetchSingleCandidate", api.candidates.getSingleCandidate);

export const fetchCreateCandidate = createAsyncThunk<
  ISingleCandidate,
  { params: IUpdateCandidateBody },
  { rejectValue: AxiosError }
>(
  "candidates/fetchCreateCandidate",
  async ({ params }, { rejectWithValue }) => {
    try {
      const { data } = await api.candidates.createCandidate(params);
      return data;
    } catch (error: unknown) {
      return rejectWithValue(error as AxiosError);
    }
  }
);

export const fetchUpdateCandidate = createAsyncThunk<
  ISingleCandidate,
  { params: IUpdateCandidateBody; id: number },
  { rejectValue: AxiosError }
>(
  "candidates/fetchUpdateCandidate",
  async ({ params, id }, { rejectWithValue }) => {
    try {
      const { data } = await api.candidates.updateCandidate(id, params);
      return data;
    } catch (error: unknown) {
      return rejectWithValue(error as AxiosError);
    }
  }
);

export const fetchDeleteCandidate = createAsyncThunkInstance(
  "candidates/fetchDeleteCandidate",
  api.candidates.deleteCandidate
);

export const fetchCandidateBlacklist = createAsyncThunk(
  "candidates/blacklist",
  async (
    { id, action }: { id: number; action: "add" | "remove" },
    { rejectWithValue }
  ) => {
    try {
      const response = await api.candidates.candidateBlacklist(id, {
        action: action as BlacklistAction,
      });
      return response.data;
    } catch (error) {
      if (error instanceof AxiosError) {
        return rejectWithValue(error.response?.data);
      }
      return rejectWithValue(error);
    }
  }
);

const candidatesSlice = createSlice({
  name: "candidates",
  initialState,
  reducers: {
    resetCurrentCandidate: (state) => {
      state.currentCandidate = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAllCandidates.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchAllCandidates.fulfilled, (state, action) => {
        state.isLoading = false;
        state.candidates = action.payload.items;
      })
      .addCase(fetchAllCandidatesByVacancy.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchAllCandidatesByVacancy.fulfilled, (state, action) => {
        state.isLoading = false;
        state.candidatesByVacancy = action.payload;
      })
      .addCase(fetchSingleCandidate.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchSingleCandidate.fulfilled, (state, action) => {
        state.isLoading = false;
        state.currentCandidate = action.payload;
      })
      .addCase(fetchCandidateBlacklist.fulfilled, (state) => {
        state.isLoading = false;
      })
      .addCase(fetchCandidateBlacklist.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });
  },
});

export const { resetCurrentCandidate } = candidatesSlice.actions;

export const candidatesReducer = candidatesSlice.reducer;
